<template>
	<main class="home">
		<section v-if="!isBannerLoading && (getBannerMulti?.main_top_slide?.length ?? 0) > 0" class="main_banner">
			<MainSwiperSlide :data="getBannerMulti?.main_top_slide ?? []" />
		</section>
		<BannerLayout v-if="getHome">
			<ResponsiveLayout class="responsive-layout">
				<MarketTimingLayout>
					<InputSearch
						:model-value="searchValue"
						:is-left="true"
						:class="{ active_search: searchValue.length > 0 }"
						:placeholder="PLACEHOLDER_SEARCH_TITLE"
						@update:model-value="onInputSearch"
						><SearchDropDown
							v-if="searchValue.length !== 0"
							:data="eventSearchData?.search_result ?? []"
							class="search_dropdown"
							@close="onClickSearchList"
					/></InputSearch>
					<BannerSub />
				</MarketTimingLayout>
			</ResponsiveLayout>

			<HomeItemLayout>
				<ExpandableTitleBar
					class="c_responsive"
					title="주간 상승률"
					:position="'center'"
					:has-view="false"
					:content="WEEKLY_INCREASE_TOOLTIP"
				/>
				<ResponsiveLayout>
					<MoreListLayout>
						<WeeklyRiseGridList
							:data="
								displayStore.isDesktop ? getHome?.weekly_rise : getHome?.weekly_rise.slice(0, moreWeeklyRiseNumber)
							"
						/>
						<MoreButton
							v-if="!displayStore.isDesktop && moreWeeklyRiseNumber < getHome?.weekly_rise.length"
							@click="onClickWeeklyRise"
						/>
					</MoreListLayout>
				</ResponsiveLayout>
			</HomeItemLayout>
			<ResponsiveLayout v-if="!isBannerLoading && (getBannerMulti?.main_top_normal?.length ?? 0) > 0">
				<section v-if="!isBannerLoading">
					<SubSwiperSlide :data="getBannerMulti?.main_top_normal ?? []" />
				</section>
			</ResponsiveLayout>

			<ResponsiveLayout>
				<HomeItemLayout v-if="getHome && getHome.hot_ranking_category && getHome.hot_ranking_category.length > 0">
					<ExpandableTitleBar title="Hot랭킹" :position="'left'" :has-view="false" :content="HOT_RANK_TOOLTIP" />
					<div class="ly_col-16">
						<HotRankGridList :data="getHome.hot_ranking_category.slice(0, moreHotRankNumber)" />
						<MoreButton v-if="moreHotRankNumber < getHome.hot_ranking_category.length" @click="onClickHotRank" />
					</div>
				</HomeItemLayout>
			</ResponsiveLayout>
			<HomeItemLayout>
				<ExpandableTitleBar
					class="c_responsive"
					title="Best 상승률 "
					highlight="Top10"
					:position="'right'"
					:has-view="false"
					:content="BEST_INCREASE_TOOLTIP"
				/>
				<MarketTimingLayout>
					<MenuTabBar
						:menu-list="['1개월', '3개월', '6개월']"
						query-key="1"
						@update:tab="onClickBestTop10Tab"
					></MenuTabBar>
					<MoreListLayout class="c_responsive">
						<ListLayout><BestIncreaseList :items="handleChangeBestTop10Data()" /></ListLayout>
						<MoreButton
							v-if="
								(selectedBestTop10Tab === '1개월'
									? getHome.best_rise_one.length
									: selectedBestTop10Tab === '3개월'
										? getHome.best_rise_thr.length
										: selectedBestTop10Tab === '6개월'
											? getHome.best_rise_six.length
											: 0) > moreBestIncreaseNumber
							"
							@click="onClickBestIncrease"
						/> </MoreListLayout
				></MarketTimingLayout>
			</HomeItemLayout>
			<ResponsiveLayout v-if="!isBannerLoading && (getBannerMulti?.main_middle?.length ?? 0) > 0">
				<section v-if="!isBannerLoading">
					<SubSwiperSlide :data="getBannerMulti?.main_middle ?? []" />
				</section>
			</ResponsiveLayout>

			<HomeItemLayout>
				<ExpandableTitleBar
					class="c_responsive"
					title="상승률 상위 업종&테마"
					:position="'right'"
					:has-view="true"
					:content="TOP_GAINING_SECTOR_THEME_TOOLTIP"
					@on-click-view-all="onClickTopGainingView"
				/>
				<MarketTimingLayout>
					<MenuTabBar :menu-list="['업종', '테마']" query-key="2"></MenuTabBar>
					<CategoryBar :items="['주간', '월간', '분기', '연간']" query-key="2" />
					<ListLayout class="c_responsive">
						<TopGainingList
							v-for="(item, idx) in riseSectorThemeData?.rise_sector_theme.slice(0, 5)"
							:key="idx"
							:data="item"
							:idx="idx + 1"
							:sort="selectedTopGainingTab"
						/>
					</ListLayout>
				</MarketTimingLayout>
			</HomeItemLayout>
			<HomeItemLayout>
				<ExpandableTitleBar
					class="c_responsive"
					title="큰손들의 실제 보유종목"
					:position="'right'"
					:has-view="false"
					:content="BIG_HOLDERS_PORTFOLIO_TOOLTIP"
				/>
				<ResponsiveLayout_PaddingLeft>
					<SlideList
						v-if="getMajorHolderNameData"
						:data="getMajorHolderNameData.major_holder_name"
						@update:major-holder-pk="onChangeMajorHolder"
					/>
				</ResponsiveLayout_PaddingLeft>
			</HomeItemLayout>
			<ResponsiveLayout v-if="!isBannerLoading && (getBannerMulti?.main_bottom?.length ?? 0) > 0">
				<section v-if="!isBannerLoading">
					<SubSwiperSlide :data="getBannerMulti?.main_bottom ?? []" />
				</section>
			</ResponsiveLayout>
			<ResponsiveLayout>
				<HomeItemLayout>
					<ExpandableTitleBar
						title="ETF"
						:position="'left'"
						:has-view="true"
						:content="ETF_TOOLTIP"
						@on-click-view-all="onClickEtfView"
					/>
					<EtfGridList :data="EtfGridData.slice(0, 6)" />
				</HomeItemLayout>
			</ResponsiveLayout>
		</BannerLayout>
	</main>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { useRoute } from 'vue-router';
import { debounce } from 'lodash';

import { useGetBannerMultiApi } from '@/api/banner';
import { useGetEventSearchApi } from '@/api/event';
import { useGetHomeApi_USA } from '@/api/home';
import { useGetMajorHolderNameApi } from '@/api/majorHolder';
import { useGetRiseSectorThemeApi } from '@/api/topGaining';
import MoreButton from '@/components/atom/button/MoreButton.vue';
import SearchDropDown from '@/components/atom/dropdown/SearchDropDown.vue';
import CategoryBar from '@/components/atom/tabBar/CategoryBar.vue';
import ExpandableTitleBar from '@/components/atom/tabBar/ExpandableTitleBar.vue';
import MenuTabBar from '@/components/atom/tabBar/MenuTabBar.vue';
import BannerSub from '@/components/banner/BannerSub.vue';
import EtfGridList from '@/components/home/EtfGridList.vue';
import HomeItemLayout from '@/components/home/HomeItemLayout.vue';
import HotRankGridList from '@/components/home/HotRankGridList.vue';
import WeeklyRiseGridList from '@/components/home/WeeklyRiseGridList.vue';
import BannerLayout from '@/components/layout/main/BannerLayout.vue';
import ListLayout from '@/components/layout/main/ListLayout.vue';
import MarketTimingLayout from '@/components/layout/main/MarketTimingLayout.vue';
import MoreListLayout from '@/components/layout/main/MoreListLayout.vue';
import ResponsiveLayout from '@/components/layout/main/ResponsiveLayout.vue';
import ResponsiveLayout_PaddingLeft from '@/components/layout/main/ResponsiveLayout_PaddingLeft.vue';
import BestIncreaseList from '@/components/rankList/BestIncreaseList.vue';
import SlideList from '@/components/rankList/SlideList.vue';
import TopGainingList from '@/components/rankList/TopGainingList.vue';
import MainSwiperSlide from '@/components/swiper/MainSwiperSlide.vue';
import SubSwiperSlide from '@/components/swiper/SubSwiperSlide.vue';
import { EtfGridData } from '@/constants/menu';
import {
	BEST_INCREASE_TOOLTIP,
	BIG_HOLDERS_PORTFOLIO_TOOLTIP,
	ETF_TOOLTIP,
	HOT_RANK_TOOLTIP,
	PLACEHOLDER_SEARCH_TITLE,
	TOP_GAINING_SECTOR_THEME_TOOLTIP,
	WEEKLY_INCREASE_TOOLTIP,
} from '@/constants/title';
import router from '@/router';
import { useDisplayStore } from '@/stores/display';
import { handleChangeTopGainingCategory } from '@/utils/function/convertValue';

// -----variable-----
const displayStore = useDisplayStore();
const route = useRoute();

const searchValue = ref('');
const moreWeeklyRiseNumber = ref(6);
const moreBestIncreaseNumber = ref(5);
const moreHotRankNumber = ref(6);

const selectedTopGainingTab = computed(() => (route.query.tab2 ? String(route.query.tab2) : '업종'));
const selectedTopGainingCategory = computed(() => (route.query.sort2 ? String(route.query.sort2) : '주간'));
const selectedBestTop10Tab = computed(() => (route.query.tab1 ? String(route.query.tab1) : '1개월'));

// -----api-----
const { data: getHome } = useGetHomeApi_USA();
const { data: riseSectorThemeData } = useGetRiseSectorThemeApi(
	computed(() => ({
		home_yn: 1,
		category: selectedTopGainingTab.value === '업종' ? 'sector' : 'theme',
		period: handleChangeTopGainingCategory(selectedTopGainingCategory.value),
	}))
);
const { data: getBannerMulti, isLoading: isBannerLoading } = useGetBannerMultiApi({
	type: 'main_top_slide,main_top_normal,main_middle,main_bottom',
});
const { data: eventSearchData } = useGetEventSearchApi(
	computed(() => ({ search: searchValue.value })),
	computed(() => !!searchValue.value)
);
const { data: getMajorHolderNameData } = useGetMajorHolderNameApi();

// -----function-----

const onClickBestTop10Tab = () => {
	moreBestIncreaseNumber.value = 5;
};
const handleChangeBestTop10Data = () => {
	if (selectedBestTop10Tab.value === '1개월') {
		return getHome.value?.best_rise_one.slice(0, moreBestIncreaseNumber.value);
	} else if (selectedBestTop10Tab.value === '3개월') {
		return getHome.value?.best_rise_thr.slice(0, moreBestIncreaseNumber.value);
	} else if (selectedBestTop10Tab.value === '6개월') {
		return getHome.value?.best_rise_six.slice(0, moreBestIncreaseNumber.value);
	} else {
		return [];
	}
};
const onInputSearch = debounce(
	(val: string) => {
		searchValue.value = val;
	},
	200,
	{ leading: false }
);
const onClickTopGainingView = () => {
	router.push('/topgaining');
};
const onClickEtfView = () => {
	router.push('/etf');
};
const onClickHotRank = () => {
	moreHotRankNumber.value = moreHotRankNumber.value + 6;
};
const onClickWeeklyRise = () => {
	moreWeeklyRiseNumber.value = moreWeeklyRiseNumber.value + 3;
};
const onClickBestIncrease = () => {
	moreBestIncreaseNumber.value = moreBestIncreaseNumber.value + 5;
};

const onClickSearchList = () => {
	searchValue.value = '';
};
const onChangeMajorHolder = async (pk: number) => {
	await router.push({
		name: 'holdings',
		query: { pk },
	});
};
</script>
<style lang="scss" scoped>
.home {
	background-color: var(--color-bg);
	padding-top: var(--header-height);
	@media screen and (max-width: 1280px) {
		padding-top: var(--mobile-header-height);
	}
	.main_banner {
		:deep(.swiper) {
			height: 100%;
			.swiper-slide > img {
				height: 60rem;
				@media screen and (max-width: 1280px) {
					height: 40rem;
				}
			}
		}
	}

	.responsive-layout {
		position: relative;
		.search_dropdown {
			position: absolute;
			top: 4.8rem;
			background: var(--color-bg);
			z-index: 10;
			border: 1px solid var(--color-primary);
			border-radius: 0rem 0rem 0.8rem 0.8rem;
			overflow: hidden;
		}
	}

	article {
		display: flex;
		flex-direction: column;
		gap: 8.8rem;
		max-width: 68rem;
		width: 100%;
		margin: 0 auto;
		padding-top: 8.8rem;
		padding-bottom: 8.8rem;
		@media screen and (max-width: 1280px) {
			padding-top: 8rem;
			padding-bottom: 8rem;
			gap: 8rem;
		}
		.banner_sub {
			img {
				cursor: pointer;
				object-fit: fill;
				border-radius: 0.8rem;
				@media screen and (max-width: 1280px) {
					height: 12rem;
				}
			}
			@media screen and (max-width: 1280px) {
				padding-left: var(--space-large);
				padding-right: var(--space-large);
			}
		}
		.search {
			display: flex;
			flex-direction: column;
			gap: var(--space-large);
			font-weight: var(--font-w-semi-bold);
			font-size: var(--font-s-x-large);
			color: var(--color-base-white);
			@media screen and (max-width: 1280px) {
				padding-left: var(--space-large);
				padding-right: var(--space-large);
			}
		}
		.best_banner {
			cursor: pointer;
			@media screen and (max-width: 1280px) {
				padding-left: var(--space-large);
				padding-right: var(--space-large);
			}
		}
	}
}
.banner_fixed {
	@media screen and (max-width: 1280px) {
		height: 8.2rem;
	}
}
</style>
